import { AdviserRole } from './domain.js';
import { householdGoalSchema, householdProjectionRequestSchema, scenarioDefinitionSchema, } from './schemas-household.js';
import { API_VERSION } from './envelope.js';
import { adviserProfileSchema, assetCreateSchema, assetSchema, assumptionsSchema, clientCreateSchema, clientPlanSchema, clientReadSchema, firmSchema, incomeStreamCreateSchema, incomeStreamSchema, liabilityCreateSchema, liabilitySchema, lifeEventSchema, projectionRequestSchema, reportExportSchema, scenarioCreateSchema, scenarioSchema, spendingItemCreateSchema, spendingItemSchema, wealthProjectionResultSchema, wealthProjectionYearSchema, } from './schemas.js';
/**
 * Deterministic fixture builders. These intentionally use fixed ids so tests
 * are reproducible. NEVER seed real client data anywhere. Richer fixtures live
 * in @cf/test-fixtures; these are the primitives it builds on.
 */
export const FIXED_FIRM_ID = '00000000-0000-0000-0000-0000000000f1';
export const FIXED_ADVISER_ID = '00000000-0000-0000-0000-0000000000a1';
export const FIXED_CLIENT_ID = '00000000-0000-0000-0000-0000000000c1';
export function makeSession(overrides = {}) {
    return {
        firmId: FIXED_FIRM_ID,
        adviserId: FIXED_ADVISER_ID,
        role: AdviserRole.Owner,
        mfaVerified: true,
        ...overrides,
    };
}
// --- Auth flow fixtures (deterministic; NEVER a real token/secret) -----------
/** A completed sign-in: access + ID-token bearer + its TTL. No refresh token. */
export const FIXTURE_LOGIN_OK = {
    status: 'ok',
    accessToken: 'fixture-access-token',
    idToken: 'fixture-id-token',
    tokenType: 'Bearer',
    expiresIn: 3600,
};
/** Login step for a returning user: answer the TOTP code, no token yet. */
export const FIXTURE_LOGIN_MFA_REQUIRED = {
    status: 'mfa_required',
    session: 'fixture-mfa-session',
};
/** Login step for a first-login user: enrol the QR secret, no token yet. */
export const FIXTURE_LOGIN_MFA_SETUP = {
    status: 'mfa_setup',
    secret: 'JBSWY3DPEHPK3PXP',
    session: 'fixture-setup-session',
};
/** The shared body posted to either challenge endpoint (/auth/mfa[-setup]). */
export const FIXTURE_RESPOND_CHALLENGE_REQUEST = {
    email: 'adviser@example.com',
    session: 'fixture-mfa-session',
    code: '123456',
};
export function makeMeta(overrides = {}) {
    return { requestId: 'test-request-id', version: API_VERSION, ...overrides };
}
export function makeEnvelope(data, overrides = {}) {
    return { data, meta: makeMeta(overrides), errors: [] };
}
/**
 * A valid create-client body, parsed through the schema so it is guaranteed to
 * match the contract (branding applied). Override any field for negative tests.
 */
export function makeClientCreate(overrides = {}) {
    return clientCreateSchema.parse({
        displayName: 'Test Client',
        dateOfBirth: '1980-01-01',
        taxRegion: 'rUK',
        ...overrides,
    });
}
/** A client as returned on read, parsed through the schema. */
export function makeClientRead(overrides = {}) {
    return clientReadSchema.parse({
        id: FIXED_CLIENT_ID,
        firmId: FIXED_FIRM_ID,
        displayName: 'Test Client',
        dateOfBirth: '1980-01-01',
        status: 'active',
        taxRegion: 'rUK',
        createdAt: '2026-07-05T00:00:00.000Z',
        updatedAt: '2026-07-05T00:00:00.000Z',
        deletedAt: null,
        ...overrides,
    });
}
/** A firm as returned on read, parsed through the schema. */
export function makeFirm(overrides = {}) {
    return firmSchema.parse({
        id: FIXED_FIRM_ID,
        name: 'Test Firm Financial Planning',
        logoUrl: null,
        brandColourHex: '#1a2b3c',
        tier: 'starter',
        ...overrides,
    });
}
/** An adviser profile as returned on read, parsed through the schema. */
export function makeAdviserProfile(overrides = {}) {
    return adviserProfileSchema.parse({
        adviserId: FIXED_ADVISER_ID,
        firmId: FIXED_FIRM_ID,
        displayName: 'Test Adviser',
        email: 'adviser@example.test',
        role: AdviserRole.Adviser,
        ...overrides,
    });
}
/**
 * A sensible default assumption set (integer basis points and integer years),
 * parsed through the schema so it always matches the contract.
 */
export function makeAssumptions(overrides = {}) {
    return assumptionsSchema.parse({
        growthBpsByWrapper: {
            cash: 150,
            cash_isa: 200,
            stocks_isa: 450,
            gia: 400,
            dc_pension: 500,
            db_pension: 0,
            state_pension: 0,
            property: 250,
            lisa: 450,
            jisa: 450,
            nsi: 150,
            business: 0,
            other_asset: 0,
        },
        inflationBps: 250,
        wageGrowthBps: 300,
        retirementAge: 62,
        statePensionAge: 68,
        horizonYears: 45,
        ...overrides,
    });
}
// --- plan-object fixtures ---------------------------------------------------
// All ids are FIXED hex uuids (no randomness, no clocks) so tests and golden
// fixtures are byte-for-byte reproducible.
export const FIXED_ASSET_ID = '00000000-0000-0000-0000-00000000a5e1';
export const FIXED_INCOME_STREAM_ID = '00000000-0000-0000-0000-000000001c01';
export const FIXED_SPENDING_ITEM_ID = '00000000-0000-0000-0000-000000005e01';
export const FIXED_LIABILITY_ID = '00000000-0000-0000-0000-00000000d0b1';
export const FIXED_LIFE_EVENT_ID = '00000000-0000-0000-0000-000000001f01';
export const FIXED_SCENARIO_ID = '00000000-0000-0000-0000-000000005c01';
/** A held asset, parsed through the schema. */
export function makeAsset(overrides = {}) {
    return assetSchema.parse({
        id: FIXED_ASSET_ID,
        clientId: FIXED_CLIENT_ID,
        wrapper: 'stocks_isa',
        label: 'Stocks & Shares ISA',
        currentAmountPence: 8_600_000,
        contributionPence: 50_000,
        contributionFrequency: 'monthly',
        ...overrides,
    });
}
/** A recurring inflow stream, parsed through the schema. */
export function makeIncomeStream(overrides = {}) {
    return incomeStreamSchema.parse({
        id: FIXED_INCOME_STREAM_ID,
        clientId: FIXED_CLIENT_ID,
        kind: 'salary',
        label: 'Salary',
        amountPence: 7_800_000,
        frequency: 'annually',
        startYear: null,
        endYear: null,
        endsAtRetirement: true,
        ...overrides,
    });
}
/** A recurring spending line, parsed through the schema. */
export function makeSpendingItem(overrides = {}) {
    return spendingItemSchema.parse({
        id: FIXED_SPENDING_ITEM_ID,
        clientId: FIXED_CLIENT_ID,
        kind: 'essential',
        label: 'Essential spending',
        amountPence: 260_000,
        frequency: 'monthly',
        startYear: null,
        endYear: null,
        ...overrides,
    });
}
/** An outstanding liability, parsed through the schema. */
export function makeLiability(overrides = {}) {
    return liabilitySchema.parse({
        id: FIXED_LIABILITY_ID,
        clientId: FIXED_CLIENT_ID,
        kind: 'mortgage',
        label: 'Mortgage',
        outstandingPence: 18_000_000,
        interestRateBps: 399,
        repaymentPence: 115_000,
        endYear: 2041,
        ...overrides,
    });
}
/** A one-off life event, parsed through the schema. */
export function makeLifeEvent(overrides = {}) {
    return lifeEventSchema.parse({
        id: FIXED_LIFE_EVENT_ID,
        clientId: FIXED_CLIENT_ID,
        kind: 'inheritance',
        label: 'Inheritance',
        year: 2035,
        amountPence: 15_000_000,
        direction: 'inflow',
        wrapper: 'gia',
        note: null,
        ...overrides,
    });
}
/** A named what-if scenario, parsed through the schema. */
export function makeScenario(overrides = {}) {
    return scenarioSchema.parse({
        id: FIXED_SCENARIO_ID,
        clientId: FIXED_CLIENT_ID,
        name: 'Base case',
        description: null,
        assumptionOverrides: {},
        addedLifeEvents: [],
        ...overrides,
    });
}
// --- fact-find (DB-aligned) fixtures ------------------------------------------
/** A valid create-scenario body, parsed through the schema. */
export function makeScenarioCreate(overrides = {}) {
    return scenarioCreateSchema.parse({
        name: 'Baseline',
        isBaseline: true,
        startYear: 2026,
        projectionYears: 40,
        // A client whose first review is still open — override for stale-tab cases.
        expectedReviewEpoch: 0,
        ...overrides,
    });
}
/** A valid create-asset body (scenario/firm come from path/session). */
export function makeAssetCreate(overrides = {}) {
    return assetCreateSchema.parse({
        owner: 'client',
        type: 'stocks_isa',
        name: 'Stocks & Shares ISA',
        currentValuePence: 8_600_000,
        monthlyContributionPence: 50_000,
        ...overrides,
    });
}
/** A valid create-income-stream body. */
export function makeIncomeStreamCreate(overrides = {}) {
    return incomeStreamCreateSchema.parse({
        owner: 'client',
        type: 'employment',
        name: 'Salary',
        amountPence: 650_000,
        frequency: 'monthly',
        ...overrides,
    });
}
/** A valid create-spending-item body. */
export function makeSpendingItemCreate(overrides = {}) {
    return spendingItemCreateSchema.parse({
        category: 'essential',
        name: 'Essential spending',
        amountPence: 260_000,
        frequency: 'monthly',
        ...overrides,
    });
}
/** A valid create-liability body. */
export function makeLiabilityCreate(overrides = {}) {
    return liabilityCreateSchema.parse({
        owner: 'joint',
        type: 'mortgage',
        name: 'Mortgage',
        balancePence: 18_000_000,
        interestRateBps: 399,
        monthlyPaymentPence: 115_000,
        endDate: '2041-06-30',
        ...overrides,
    });
}
// --- wealth projection + report fixtures ------------------------------------
/** The demo plan is anchored here: the client is 47 as of the 2026 start. */
export const DEMO_START_YEAR = 2026;
export const DEMO_CURRENT_AGE = 47;
/** An empty client plan, parsed through the schema. */
export function makeClientPlan(overrides = {}) {
    return clientPlanSchema.parse({
        clientId: FIXED_CLIENT_ID,
        assets: [],
        incomeStreams: [],
        spendingItems: [],
        liabilities: [],
        lifeEvents: [],
        ...overrides,
    });
}
/**
 * The workhorse demo fixture: a representative 47-year-old client's full plan
 * (as of `DEMO_START_YEAR`), every figure a fixed integer of pence and every
 * id a fixed uuid — no randomness, no clocks. Pairs with `makeAssumptions()`
 * (retirement 62, State Pension age 68). DB pension starts at age 65 (2044);
 * State Pension at 68 (2047).
 */
export function makeDemoPlan(clientId = FIXED_CLIENT_ID) {
    return clientPlanSchema.parse({
        clientId,
        assets: [
            {
                id: '00000000-0000-0000-0000-00000000a5e1',
                clientId,
                wrapper: 'cash',
                label: 'Current account & savings',
                currentAmountPence: 1_800_000,
                contributionPence: 0,
                contributionFrequency: 'annually',
            },
            {
                id: '00000000-0000-0000-0000-00000000a5e2',
                clientId,
                wrapper: 'cash_isa',
                label: 'Cash ISA',
                currentAmountPence: 2_200_000,
                contributionPence: 0,
                contributionFrequency: 'annually',
            },
            {
                id: '00000000-0000-0000-0000-00000000a5e3',
                clientId,
                wrapper: 'stocks_isa',
                label: 'Stocks & Shares ISA',
                currentAmountPence: 8_600_000,
                contributionPence: 50_000,
                contributionFrequency: 'monthly',
            },
            {
                id: '00000000-0000-0000-0000-00000000a5e4',
                clientId,
                wrapper: 'gia',
                label: 'General Investment Account',
                currentAmountPence: 4_500_000,
                contributionPence: 0,
                contributionFrequency: 'annually',
            },
            {
                id: '00000000-0000-0000-0000-00000000a5e5',
                clientId,
                wrapper: 'dc_pension',
                label: 'Workplace DC pension',
                currentAmountPence: 31_000_000,
                contributionPence: 120_000,
                contributionFrequency: 'monthly',
            },
            {
                id: '00000000-0000-0000-0000-00000000a5e6',
                clientId,
                wrapper: 'property',
                label: 'Main residence',
                currentAmountPence: 42_500_000,
                contributionPence: 0,
                contributionFrequency: 'annually',
            },
        ],
        incomeStreams: [
            {
                id: '00000000-0000-0000-0000-000000001c01',
                clientId,
                kind: 'salary',
                label: 'Salary',
                amountPence: 7_800_000,
                frequency: 'annually',
                startYear: null,
                endYear: null,
                endsAtRetirement: true,
            },
            {
                id: '00000000-0000-0000-0000-000000001c02',
                clientId,
                kind: 'pension_income',
                label: 'DB pension (from age 65)',
                amountPence: 900_000,
                frequency: 'annually',
                startYear: 2044,
                endYear: null,
                endsAtRetirement: false,
            },
            {
                id: '00000000-0000-0000-0000-000000001c03',
                clientId,
                kind: 'pension_income',
                label: 'State Pension (from age 68)',
                amountPence: 1_150_200,
                frequency: 'annually',
                startYear: 2047,
                endYear: null,
                endsAtRetirement: false,
            },
        ],
        spendingItems: [
            {
                id: '00000000-0000-0000-0000-000000005e01',
                clientId,
                kind: 'essential',
                label: 'Essential spending',
                amountPence: 260_000,
                frequency: 'monthly',
                startYear: null,
                endYear: null,
            },
            {
                id: '00000000-0000-0000-0000-000000005e02',
                clientId,
                kind: 'lifestyle',
                label: 'Lifestyle spending',
                amountPence: 140_000,
                frequency: 'monthly',
                startYear: null,
                endYear: null,
            },
            {
                id: '00000000-0000-0000-0000-000000005e03',
                clientId,
                kind: 'essential',
                label: 'School fees',
                amountPence: 150_000,
                frequency: 'monthly',
                startYear: null,
                endYear: 2029,
            },
        ],
        liabilities: [
            {
                id: '00000000-0000-0000-0000-00000000d0b1',
                clientId,
                kind: 'mortgage',
                label: 'Mortgage',
                outstandingPence: 18_000_000,
                interestRateBps: 399,
                repaymentPence: 115_000,
                endYear: 2041,
            },
        ],
        lifeEvents: [
            {
                id: '00000000-0000-0000-0000-000000001f01',
                clientId,
                kind: 'one_off_expense',
                label: 'Kitchen extension',
                year: 2027,
                amountPence: 4_000_000,
                direction: 'outflow',
                wrapper: 'cash',
                note: null,
            },
            {
                id: '00000000-0000-0000-0000-000000001f02',
                clientId,
                kind: 'inheritance',
                label: 'Inheritance',
                year: 2035,
                amountPence: 15_000_000,
                direction: 'inflow',
                wrapper: 'gia',
                note: null,
            },
        ],
    });
}
/**
 * One projected year, parsed through the schema. Defaults mirror the demo
 * plan's opening position at `DEMO_START_YEAR`.
 */
export function makeWealthProjectionYear(overrides = {}) {
    return wealthProjectionYearSchema.parse({
        year: DEMO_START_YEAR,
        age: DEMO_CURRENT_AGE,
        balancesByWrapper: {
            cash: 1_800_000,
            cash_isa: 2_200_000,
            stocks_isa: 8_600_000,
            gia: 4_500_000,
            dc_pension: 31_000_000,
            db_pension: 0,
            state_pension: 0,
            property: 42_500_000,
            lisa: 0,
            jisa: 0,
            nsi: 0,
            business: 0,
            other_asset: 0,
        },
        liabilitiesOutstandingPence: 18_000_000,
        totalNetWorthPence: 72_600_000,
        grossIncomePence: 7_800_000,
        spendingPence: 6_600_000,
        illustrativeTaxPence: 1_900_000,
        ...overrides,
    });
}
/** A wealth projection result carrying one demo year, parsed through the schema. */
export function makeWealthProjectionResult(overrides = {}) {
    return wealthProjectionResultSchema.parse({
        engineVersion: '0.0.0-fixture',
        taxRulesVersion: '2025-26.fixture',
        asOfDate: '2026-04-06',
        years: [makeWealthProjectionYear()],
        depletionYear: null,
        warnings: [],
        ...overrides,
    });
}
/** A full report-export view model, parsed through the schema. */
export function makeReportExport(overrides = {}) {
    return reportExportSchema.parse({
        client: makeClientRead(),
        firm: makeFirm(),
        adviser: makeAdviserProfile(),
        scenarioName: null,
        generatedAsOf: '2026-04-06',
        engineVersion: '0.0.0-fixture',
        taxRulesVersion: '2025-26.fixture',
        assumptions: makeAssumptions(),
        projection: makeWealthProjectionResult(),
        auditReference: 'AUDIT-PENDING',
        disclaimerVersion: 'v1',
        ...overrides,
    });
}
/**
 * A valid projection request (mirrors the E.1 golden-fixture flavour), parsed
 * through the schema. Structurally hands to the engine's `project()`.
 */
export function makeProjectionRequest(overrides = {}) {
    return projectionRequestSchema.parse({
        horizonYears: 5,
        openingBalance: 5_000_000,
        incomes: [
            { label: 'Salary', amount: 200_000, frequency: 'monthly', startYear: 2025, endYear: 2027 },
        ],
        expenses: [
            {
                label: 'Living costs',
                amount: 150_000,
                frequency: 'monthly',
                startYear: 2025,
                endYear: null,
            },
        ],
        ...overrides,
    });
}
/**
 * Deterministic household-projection request covering every Stage 4 asset
 * type (13 wrappers), a partner, a dependent with a JISA, employer DC
 * contributions, and every liability kind — no real client data.
 */
export function makeHouseholdProjectionRequest(overrides = {}) {
    return householdProjectionRequestSchema.parse({
        clientId: FIXED_CLIENT_ID,
        asOfDate: '2025-04-06',
        startYear: 2025,
        people: [
            {
                id: 'client',
                currentAge: 45,
                retirementAge: 67,
                statePensionAge: 67,
                deathAge: null,
                taxRegion: 'rUK',
            },
            {
                id: 'partner',
                currentAge: 43,
                retirementAge: 67,
                statePensionAge: 67,
                deathAge: null,
                taxRegion: 'rUK',
            },
        ],
        dependents: [{ id: 'dep-1', label: 'Eldest child', dateOfBirth: '2018-09-01' }],
        incomeStreams: [
            {
                id: 'inc-1',
                label: 'Salary',
                ownerId: 'client',
                type: 'employment',
                amount: 65_000_00,
                frequency: 'annually',
                startYear: null,
                endYear: null,
                endsAtRetirement: true,
                growthRateBps: null,
                taxable: true,
            },
        ],
        assets: [
            {
                id: 'a-cash',
                label: 'Current account',
                ownerId: 'joint',
                wrapper: 'cash',
                currentValue: 25_000_00,
                contribution: 0,
                contributionFrequency: 'annually',
                growthRateBps: null,
            },
            {
                id: 'a-lisa',
                label: 'Lifetime ISA',
                ownerId: 'client',
                wrapper: 'lisa',
                currentValue: 12_000_00,
                contribution: 4_000_00,
                contributionFrequency: 'annually',
                growthRateBps: null,
            },
            {
                id: 'a-jisa',
                label: 'Junior ISA (Eldest)',
                ownerId: 'client',
                wrapper: 'jisa',
                dependentId: 'dep-1',
                currentValue: 5_000_00,
                contribution: 100_00,
                contributionFrequency: 'monthly',
                growthRateBps: null,
            },
            {
                id: 'a-nsi',
                label: 'Premium bonds',
                ownerId: 'partner',
                wrapper: 'nsi',
                currentValue: 20_000_00,
                contribution: 0,
                contributionFrequency: 'annually',
                growthRateBps: 350,
            },
            {
                id: 'a-dc-1',
                label: 'Workplace pension',
                ownerId: 'client',
                wrapper: 'dc_pension',
                currentValue: 180_000_00,
                contribution: 400_00,
                contributionFrequency: 'monthly',
                employerContribution: 600_00,
                employerContributionFrequency: 'monthly',
                accessAge: 60,
                growthRateBps: null,
            },
            {
                id: 'a-business',
                label: 'Trading company shares',
                ownerId: 'client',
                wrapper: 'business',
                currentValue: 250_000_00,
                contribution: 0,
                contributionFrequency: 'annually',
                growthRateBps: 300,
                yieldBps: 200,
                bprRateBps: 10_000,
                notes: 'BPR-qualifying assumed; adviser to confirm.',
            },
            {
                id: 'a-car',
                label: 'Family car',
                ownerId: 'joint',
                wrapper: 'other_asset',
                currentValue: 18_000_00,
                contribution: 0,
                contributionFrequency: 'annually',
                growthRateBps: -1500,
                cgtChattelsExempt: true,
            },
            {
                id: 'a-home',
                label: 'Main home',
                ownerId: 'joint',
                wrapper: 'property',
                currentValue: 450_000_00,
                contribution: 0,
                contributionFrequency: 'annually',
                growthRateBps: null,
                property: {
                    isBuyToLet: false,
                    isPrimaryResidence: true,
                    rentalYieldBps: 0,
                    linkedLiabilityId: 'l-mortgage',
                },
            },
        ],
        liabilities: [
            {
                id: 'l-mortgage',
                label: 'Repayment mortgage',
                kind: 'mortgage',
                balance: 220_000_00,
                interestRateBps: 425,
                monthlyPayment: 1_250_00,
                interestOnly: false,
                endYear: null,
                rateChanges: [{ fromYear: 2028, interestRateBps: 500 }],
            },
            {
                id: 'l-car',
                label: 'Car finance',
                kind: 'loan',
                balance: 9_000_00,
                interestRateBps: 690,
                monthlyPayment: 55_00,
                interestOnly: true,
                endYear: 2028,
            },
        ],
        spending: [
            {
                id: 'sp-1',
                label: 'Living costs',
                amount: 3_200_00,
                frequency: 'monthly',
                startYear: null,
                endYear: null,
                inflationLinked: true,
            },
        ],
        lifeEvents: [],
        plannedEvents: [
            {
                kind: 'schoolFees',
                id: 'pe-school',
                label: 'School fees (Eldest)',
                dependentId: 'dep-1',
                feePerTerm: 5_500_00,
                startAge: 7,
                endAge: 13,
                siblingDiscountBps: 0,
            },
            {
                kind: 'university',
                id: 'pe-uni',
                label: 'University (Eldest)',
                dependentId: 'dep-1',
                annualTuition: 9_535_00,
                annualMaintenance: 10_000_00,
                parentalContributionBps: 5000,
            },
            {
                kind: 'recurring',
                id: 'pe-car',
                label: 'Replace car',
                amount: 25_000_00,
                everyNYears: 6,
                startYear: 2028,
                escalation: { kind: 'cpiPlus', deltaBps: 0 },
            },
            {
                kind: 'care',
                id: 'pe-care',
                label: 'Residential care (client)',
                personId: 'client',
                startAge: 86,
                durationYears: 3,
                annualCost: 55_000_00,
            },
        ],
        assumptions: { preset: 'moderate' },
        window: { endAtClientAge: 90 },
        giaCgtMode: 'onWithdrawal',
        ...overrides,
    });
}
/**
 * A plan where nothing takes anything away: income and assets are captured
 * but there is no spending, no liability and no outgoing event. Drives the
 * plan-completeness prompts (PLAN_COMPLETENESS_WARNING_CODES) in engine,
 * contract and API tests — an over-high projection from this shape is a
 * fact-find gap, not an engine defect.
 */
export function makeNoOutflowsHouseholdProjectionRequest(overrides = {}) {
    return householdProjectionRequestSchema.parse({
        clientId: FIXED_CLIENT_ID,
        asOfDate: '2025-04-06',
        startYear: 2025,
        people: [
            {
                id: 'client',
                currentAge: 45,
                retirementAge: 67,
                statePensionAge: 67,
                deathAge: null,
                taxRegion: 'rUK',
            },
        ],
        incomeStreams: [
            {
                id: 'inc-1',
                label: 'Salary',
                ownerId: 'client',
                type: 'employment',
                amount: 65_000_00,
                frequency: 'annually',
                startYear: null,
                endYear: null,
                endsAtRetirement: true,
                growthRateBps: null,
                taxable: true,
            },
        ],
        assets: [
            {
                id: 'a-cash',
                label: 'Current account',
                ownerId: 'client',
                wrapper: 'cash',
                currentValue: 25_000_00,
                contribution: 0,
                contributionFrequency: 'annually',
                growthRateBps: null,
            },
            {
                id: 'a-isa',
                label: 'S&S ISA',
                ownerId: 'client',
                wrapper: 'stocks_isa',
                currentValue: 40_000_00,
                contribution: 5_000_00,
                contributionFrequency: 'annually',
                growthRateBps: null,
            },
        ],
        liabilities: [],
        spending: [],
        assumptions: { preset: 'moderate' },
        window: { endAtClientAge: 90 },
        ...overrides,
    });
}
/**
 * Deterministic saved scenario definition for the Cash Flow Modeller: an assumption
 * delta, a dated one-off gift, a school-fees plan with a stepped escalation
 * (two fee rises inside one scenario) and a year-anchored market shock —
 * no real client data.
 */
export function makeScenarioDefinition(overrides = {}) {
    return scenarioDefinitionSchema.parse({
        assumptionOverrides: { inflationBps: 350 },
        addLifeEvents: [
            {
                id: 'lab-gift-2030',
                label: 'One-off gift to daughter',
                year: 2030,
                amount: 25_000_00,
                direction: 'outflow',
                wrapper: null,
                isGift: true,
            },
        ],
        addPlannedEvents: [
            {
                kind: 'schoolFees',
                id: 'lab-school-d1',
                label: 'School fees (eldest)',
                dependentId: 'd1',
                feePerTerm: 6_000_00,
                startYear: 2026,
                endYear: 2033,
                escalation: {
                    kind: 'stepped',
                    steps: [
                        { fromYear: 2026, rate: { kind: 'fixed', rateBps: 500 } },
                        { fromYear: 2029, rate: { kind: 'fixed', rateBps: 800 } },
                        { fromYear: 2031, rate: { kind: 'cpiPlus', deltaBps: 100 } },
                    ],
                },
            },
        ],
        addMarketShocks: [
            {
                id: 'lab-crash-2027',
                label: 'Market fall of 20% in 2027',
                year: 2027,
                dropBpsByWrapper: { stocks_isa: 2000, gia: 2000, dc_pension: 2000 },
            },
        ],
        ...overrides,
    });
}
/** Deterministic firm assumption presets (Firm settings tab tests). */
export const FIXTURE_FIRM_ASSUMPTION_PRESETS = {
    conservative: null,
    moderate: {
        inflationBps: 250,
        rpiBps: 350,
        wageGrowthBps: 350,
        equity: { growthBps: 480, yieldBps: 210 },
        bonds: { growthBps: 280, yieldBps: 310 },
        cash: { growthBps: 190, yieldBps: 0 },
        property: { growthBps: 280, yieldBps: 0 },
        defaultFeeBps: 45,
        // A firm's own name for the set (0028), exercised here so every consumer
        // of this fixture renders the renamed case rather than only the default.
        displayLabel: 'House Balanced',
    },
    optimistic: null,
    // One firm-created set (0030), so every consumer of this fixture renders the
    // custom case too — a firm's own named set is the round-6 feature, and a
    // fixture that only ever held the three built-ins would exercise none of it.
    custom: [
        {
            key: 'custom:3f8c1a92-5d47-4b6e-9c21-7ae0f4d81b53',
            inflationBps: 250,
            rpiBps: 350,
            wageGrowthBps: 350,
            equity: { growthBps: 520, yieldBps: 200 },
            bonds: { growthBps: 300, yieldBps: 300 },
            cash: { growthBps: 200, yieldBps: 0 },
            property: { growthBps: 300, yieldBps: 0 },
            defaultFeeBps: 50,
            displayLabel: 'Cantab Balanced',
        },
    ],
};
/**
 * Client goals (2026-09-03): one of each v1 mechanic, against the household
 * fixture above — a "£X by age Y" pension target for the client, and a
 * fund-if-surplus Junior ISA goal for the fixture's dependent. The JISA goal
 * pairs with a `jisa` sweep step carrying `goalId: 'goal-jisa'`.
 */
export function makeHouseholdGoals() {
    return [
        householdGoalSchema.parse({
            kind: 'amountAtAge',
            id: 'goal-pension',
            label: '£500,000 in pensions by 60',
            source: 'client',
            anchor: { kind: 'personAge', personId: 'client', age: 60 },
            target: 500_000_00,
            terms: 'today',
            series: { kind: 'wrapper', wrapper: 'dc_pension' },
        }),
        householdGoalSchema.parse({
            kind: 'fundFromSurplus',
            id: 'goal-jisa',
            label: 'Put £3,000 a year into the Junior ISA if there is room',
            source: 'client',
            target: 'jisa',
            dependentId: 'dep-1',
            assetId: 'a-jisa',
            amount: 3_000_00,
        }),
    ];
}
/** One goal of every kind and every fund-if-surplus target, against the household fixture. */
export function makeHouseholdGoalsFull() {
    return [
        ...makeHouseholdGoals(),
        householdGoalSchema.parse({
            kind: 'amountAtAge',
            id: 'goal-legacy',
            label: 'Leave £200,000 to the children',
            source: 'client',
            anchor: { kind: 'endOfPlan' },
            target: 200_000_00,
            terms: 'today',
            series: { kind: 'legacy' },
        }),
        householdGoalSchema.parse({
            kind: 'spendingWindow',
            id: 'goal-income',
            label: '£45,000 a year after tax from 67 to 80',
            source: 'client',
            amount: 45_000_00,
            basis: 'net',
            terms: 'today',
            fromAnchor: { kind: 'personAge', personId: 'client', age: 67 },
            toAnchor: { kind: 'personAge', personId: 'client', age: 80 },
        }),
        householdGoalSchema.parse({
            kind: 'liabilityClearBy',
            id: 'goal-mortgage',
            label: 'Mortgage clear by 60',
            source: 'client',
            liabilityId: 'l-mortgage',
            anchor: { kind: 'personAge', personId: 'client', age: 60 },
        }),
        householdGoalSchema.parse({
            kind: 'fundFromSurplus',
            id: 'goal-pension-extra',
            label: 'Extra £5,000 a year into the pension when there is room',
            source: 'client',
            target: 'pension',
            personId: 'client',
            amount: 5_000_00,
        }),
        householdGoalSchema.parse({
            kind: 'fundFromSurplus',
            id: 'goal-holiday',
            label: 'The big holiday, £4,000 a year, when there is room',
            source: 'client',
            target: 'spend',
            purpose: 'the yearly holiday',
            amount: 4_000_00,
        }),
        householdGoalSchema.parse({
            kind: 'fundFromSurplus',
            id: 'goal-deposit',
            label: 'Help with a deposit, £20,000, in 2035 if there is room',
            source: 'client',
            target: 'spend',
            purpose: 'help with a deposit',
            isGift: true,
            oneOff: true,
            amount: 20_000_00,
            startAnchor: { kind: 'year', year: 2035 },
            endAnchor: { kind: 'year', year: 2035 },
        }),
        householdGoalSchema.parse({
            kind: 'fundFromSurplus',
            id: 'goal-overpay',
            label: 'Overpay the mortgage by £3,000 a year when there is room',
            source: 'client',
            target: 'liability',
            liabilityId: 'l-mortgage',
            amount: 3_000_00,
        }),
    ];
}
//# sourceMappingURL=fixtures.js.map